import { ChangeEvent, ChangeEventHandler, useCallback, useEffect, useMemo, useState } from 'react'
import { KeepTruckinTruckLocations } from 'apps/fleet-planner/types'

export interface ITruckList extends Partial<Record<string, boolean>> {}

export interface IUseImportTruckListFormProps {
  /**
   * Values for selected trucks
   */
  defaultValues?: KeepTruckinTruckLocations[]
}

/**
 * Returns keyed object by id, with default checked state of true
 * @example { 'A12': true, 'AB22': true }
 */
const getInitialValues = (truckLocations: KeepTruckinTruckLocations[] = []): ITruckList => {
  const stateObj: ITruckList = {}
  for (const location of truckLocations) {
    const { id } = location || {}
    if (id) {
      stateObj[id] = true
    }
  }
  return stateObj
}

/**
 * Handles logic for truck import list
 */
export const useImportTruckListForm = ({
  defaultValues: defaultValuesProp = [],
}: IUseImportTruckListFormProps) => {
  const defaultValues = getInitialValues(defaultValuesProp)
  const [selectedTrucks, setSelectedTrucks] = useState<ITruckList>({ ...defaultValues })

  /**
   * If props update, reset the local state
   */
  useEffect(() => {
    const newDefaultValues = getInitialValues(defaultValuesProp)
    setSelectedTrucks({ ...newDefaultValues })
  }, [defaultValuesProp])

  /**
   * Toggles state for keyName
   * @example changeHandler('a12')
   */
  const changeHandler = useCallback((keyName: string, newValue?: boolean) => {
    return setSelectedTrucks(prev => {
      const value = typeof newValue === 'boolean' ? newValue : !prev[keyName]
      return {
        ...prev,
        [keyName]: value,
      }
    })
  }, [])

  /**
   * Generates a change handler for inputs
   * @example const changeHandler = createChangeHandler(truckId)
   */
  const createChangeHandler = useCallback(
    (keyName: string): ChangeEventHandler<HTMLInputElement> => {
      return (event: ChangeEvent<HTMLInputElement>) => {
        changeHandler(keyName, event.target.checked)
      }
    },
    [changeHandler]
  )

  /**
   * Helper for returning boolean value if item is selected
   * @example const selected = getSelectedByKeyName(truckId) // false
   */
  const getSelectedByKeyName = useCallback(
    (keyName: string) => {
      return !!selectedTrucks[keyName]
    },
    [selectedTrucks]
  )

  /**
   * Helper to toggle all values to be true/false
   */
  const toggleAllHandler = useCallback(() => {
    return setSelectedTrucks(prevState => {
      /**
       * If there's any `false` values, toggle all to `true`
       */
      const newValue = Object.values({ ...prevState }).includes(false)
      return Object.keys({ ...prevState }).reduce((prev, current) => {
        return {
          ...prev,
          [current]: newValue,
        }
      }, {})
    })
  }, [])

  const allTrucksToggled: boolean = useMemo(
    () => !Object.values({ ...selectedTrucks }).includes(false),
    [selectedTrucks]
  )

  /**
   * Array of currently selected ids
   * @returns ['A12', 'A24'] etc.
   */
  const selectedTruckIds: string[] = useMemo(
    () =>
      Object.keys(selectedTrucks).reduce((prev: string[], curr: string) => {
        if (selectedTrucks[curr]) {
          return [...prev, curr]
        }

        return prev
      }, []),
    [selectedTrucks]
  )

  return {
    selectedTrucks,
    selectedTruckIds,
    changeHandler,
    createChangeHandler,
    getSelectedByKeyName,
    toggleAllHandler,
    allTrucksToggled,
  }
}
